import axios from 'axios';
import {
  REGISTER_REQUEST,
  REGISTER_SUCCESS,
  REGISTER_FAILURE,
  LOGIN_REQUEST,
  LOGIN_SUCCESS,
  LOGIN_FAILURE,
  LOGIN_OTP_REQUEST,
  LOGIN_OTP_SUCCESS,
  LOGIN_OTP_FAILURE,
  VERIFY_LOGIN_OTP_REQUEST,
  VERIFY_LOGIN_OTP_SUCCESS,
  VERIFY_LOGIN_OTP_FAILURE,
  RESET_LOGIN_STATE,
  GET_USER_REQUEST,
  GET_USER_SUCCESS,
  GET_USER_FAILURE,
  LOGOUT,
  GET_ALL_CUSTOMERS_REQUEST,
  GET_ALL_CUSTOMERS_SUCCESS,
  GET_ALL_CUSTOMERS_FAILURE,
  SEND_OTP_REQUEST,
  SEND_OTP_SUCCESS,
  SEND_OTP_FAILURE,
  VERIFY_OTP_REQUEST,
  VERIFY_OTP_SUCCESS,
  VERIFY_OTP_FAILURE,
  CLEAR_AUTH_ERROR
} from './ActionTypes';
import api, { API_BASE_URL } from '../../config/api';

// Register action creators
const registerRequest = () => ({ type: REGISTER_REQUEST });
const registerSuccess = (data) => ({ type: REGISTER_SUCCESS, payload: data });
const registerFailure = error => ({ type: REGISTER_FAILURE, payload: error });

export const register = userData => async dispatch => {
  dispatch(registerRequest());
  try {
    const response = await axios.post(`${API_BASE_URL}/auth/signup`, userData);
    const data = response.data;
    // No JWT is returned on registration anymore - user must login with temp password
    // Registration successful
    dispatch(registerSuccess(data));
    return { success: true, message: data.message };
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message;
    dispatch(registerFailure(errorMessage));
    return { success: false, error: errorMessage };
  }
};

// Login action creators
const loginRequest = () => ({ type: LOGIN_REQUEST });
const loginSuccess = user => ({ type: LOGIN_SUCCESS, payload: user });
const loginFailure = error => ({ type: LOGIN_FAILURE, payload: error });

// Step 1: Login - validates credentials, may send OTP or return JWT directly
export const login = userData => async dispatch => {
  dispatch({ type: LOGIN_OTP_REQUEST });
  try {
    const response = await axios.post(`${API_BASE_URL}/auth/signin`, userData);
    const data = response.data;

    // If user is already OTP verified, JWT is returned directly (no OTP step)
    if (data.jwt) {
      localStorage.setItem("jwt", data.jwt);
      dispatch({ type: LOGIN_SUCCESS, payload: data });
      return { success: true, directLogin: true, jwt: data.jwt };
    }

    // First-time user: OTP was sent
    if (data.status === "OTP_SENT") {
      dispatch({
        type: LOGIN_OTP_SUCCESS,
        payload: {
          email: data.email,
          cooldownSeconds: data.cooldownSeconds,
          password: userData.password // Store temporarily for OTP verification
        }
      });
      return { success: true, otpSent: true };
    }

    return { success: false, error: "Unexpected response" };
  } catch (error) {
    const errorMessage = error.response?.data?.message || "Invalid email or password";
    dispatch({ type: LOGIN_OTP_FAILURE, payload: errorMessage });
    return { success: false, error: errorMessage };
  }
};

// Step 2: Verify Login OTP and get JWT
export const verifyLoginOtp = (email, otp, password) => async dispatch => {
  dispatch({ type: VERIFY_LOGIN_OTP_REQUEST });
  try {
    const response = await axios.post(`${API_BASE_URL}/auth/verify-login-otp`, {
      email,
      otp,
      password
    });
    const user = response.data;

    if (user.jwt) {
      localStorage.setItem("jwt", user.jwt);
      dispatch({ type: VERIFY_LOGIN_OTP_SUCCESS, payload: user });
      return { success: true };
    }

    return { success: false, error: "No token received" };
  } catch (error) {
    const errorMessage = error.response?.data?.message || "Invalid OTP";
    dispatch({ type: VERIFY_LOGIN_OTP_FAILURE, payload: errorMessage });
    return { success: false, error: errorMessage };
  }
};

// Reset login state (for going back to step 1)
export const resetLoginState = () => ({ type: RESET_LOGIN_STATE });



//  get user from token
export const getAllCustomers = (token) => {
  return async (dispatch) => {
    dispatch({ type: GET_ALL_CUSTOMERS_REQUEST });
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/users`, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      const users = response.data;
      dispatch({ type: GET_ALL_CUSTOMERS_SUCCESS, payload: users });
    } catch (error) {
      const errorMessage = error.message;
      dispatch({ type: GET_ALL_CUSTOMERS_FAILURE, payload: errorMessage });
    }
  };
};

export const getUser = (token) => {
  return async (dispatch) => {
    dispatch({ type: GET_USER_REQUEST });
    try {
      const response = await axios.get(`${API_BASE_URL}/api/users/profile`, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      const user = response.data;
      dispatch({ type: GET_USER_SUCCESS, payload: user });
    } catch (error) {
      const errorMessage = error.message;
      dispatch({ type: GET_USER_FAILURE, payload: errorMessage });
    }
  };
};

export const logout = () => {
  return (dispatch) => {
    // Clear all storage FIRST before dispatching to prevent race conditions
    localStorage.clear();
    sessionStorage.clear();
    // Then update Redux state
    dispatch({ type: LOGOUT });
  };
};

// OTP Actions
export const sendOtp = (email) => async (dispatch) => {
  dispatch({ type: SEND_OTP_REQUEST });
  try {
    const response = await axios.post(`${API_BASE_URL}/auth/send-otp`, { email });
    dispatch({ type: SEND_OTP_SUCCESS, payload: response.data });
    return { success: true };
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message;
    dispatch({ type: SEND_OTP_FAILURE, payload: errorMessage });
    return { success: false, error: errorMessage };
  }
};

export const verifyOtp = (email, otp) => async (dispatch) => {
  dispatch({ type: VERIFY_OTP_REQUEST });
  try {
    const response = await axios.post(`${API_BASE_URL}/auth/verify-otp`, { email, otp });
    dispatch({ type: VERIFY_OTP_SUCCESS, payload: response.data });
    return { success: true };
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message;
    dispatch({ type: VERIFY_OTP_FAILURE, payload: errorMessage });
    return { success: false, error: errorMessage };
  }
};

export const clearAuthError = () => ({ type: CLEAR_AUTH_ERROR });
